import React, { useState, useEffect } from "react";
import {
  Modal,
  Alert,
  Checkbox,
  Form,
  Input,
  Radio,
  Card,
  Divider,
  Steps,
  Select,
  Tag,
  Image,
  Row,
  Col,
  List,
  Avatar,
  DatePicker,
  Dropdown,
  Button,
  Menu,
  Typography,
  Space,
  Spin,
  message,
} from "antd";
import "./RoomBooking.css";
import { InfoCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import axios from "axios";
import Navbar from "../../../layouts/navbar";
import { useRoom, Room, SelectedRate, SpecialRate } from "./RoomContext";
import { useAuth } from "../../../layouts/auth/AuthContext";
import { useNavigate } from "react-router-dom";
import { useReferral } from "../../../utils/referral";
import { useCheckout } from "../../../layouts/checkout/CheckoutContext"; // Add this import

const { Text, Link, Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Step } = Steps;
const { TextArea } = Input;

function totalDaysBetweenTwoDates(startDate: any, endDate: any) {
  let start = moment(startDate, "YYYY-MM-DD");
  let end = moment(endDate, "YYYY-MM-DD");

  return moment.duration(end.diff(start)).asDays();
}

interface BookingParams {
  roomQty: string;
  checkInDate: string;
  checkOutDate: string;
  name: string;
  email: string;
  phone: string;
  specialInstruction: string;
  childPax: number;
  adultPax: number;
}

interface CurrentRoomBookingDetails {
  selectedRoom: Room;
  selectedRate?: SelectedRate;
  bookingParams: BookingParams;
  priceType: any;
}

interface RoomCheckoutProps {
  visible?: boolean;
  onClose?: () => void;
}

const RoomCheckout = (props: RoomCheckoutProps) => {
  const {
    navigateWithReferral,
    referralCode: urlReferralCode,
    clearReferral,
  } = useReferral();
  const [form] = Form.useForm();
  const { currentRoomBookingDetails, createBooking, createPayment } = useRoom();
  const { user, isLoggedIn } = useAuth();
  const { checkPromotionEligibility } = useCheckout(); // Add this
  const [filled, setFilled] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [priceType, setPriceType] = useState(0);
  const [specialRequest, setSpecialRequest] = useState("");
  const [checkedAddons, setCheckedAddons] = useState<any>([]);
  const [checkedAddonDetails, setCheckedAddonDetails] = useState<any[]>([]);
  const [checkAllAddonsChecked, setCheckAllAddonsChecked] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [payingNow, setPayingNow] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [manualReferral, setManualReferral] = useState(""); // For manual referral input
  // const [localTax, setLocalTax] = useState(3);

  // Promo code states
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<any>(null);
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [promoError, setPromoError] = useState("");

  const finalReferralCode = urlReferralCode || manualReferral;

  const { visible, onClose } = props;

  // Calculate price based on selected rate
  const calculatePrice = () => {
    const { selectedRoom, selectedRate, bookingParams } =
      currentRoomBookingDetails || {};

    if (!selectedRoom || !bookingParams) return { perNight: 0, total: 0 };

    const totalNights = totalDaysBetweenTwoDates(
      bookingParams.checkInDate,
      bookingParams.checkOutDate
    );
    const roomQty = parseInt(bookingParams.roomQty) || 1;

    let pricePerNight = 0;

    if (
      (selectedRate?.type === "special" && selectedRate.rate) ||
      (selectedRate?.type === "rateplan" &&
        (selectedRate.specialRate ||
          selectedRate.rateplan ||
          selectedRate.rate))
    ) {
      // Use special rate if present, otherwise rateplan amount, otherwise legacy rate
      if (selectedRate?.specialRate) {
        pricePerNight = parseFloat(selectedRate.specialRate.amount);
      } else if (selectedRate?.rateplan) {
        pricePerNight = parseFloat(selectedRate.rateplan.amount);
      } else if (selectedRate?.rate) {
        pricePerNight = parseFloat(selectedRate.rate.amount);
      }
    } else {
      // Use standard rate with discount
      const originalPrice = parseFloat(
        selectedRoom.RoomPrice?.per_room_OriginalPrice || "0"
      );
      const discountPrice = parseFloat(
        selectedRoom.RoomPrice?.DiscountPrice?.toString() || "0"
      );
      const discountType = selectedRoom.RoomPrice?.DiscountType || "";

      // Extract discount rate from DiscountType (e.g., "EARLYBIRD DISCOUNT 0.45" -> 0.45 = 45%)
      const extractDiscountRate = (discountType: string): number => {
        if (!discountType) return 0;
        const match = discountType.match(/DISCOUNT\s+([\d.]+)/i);
        if (match && match[1]) {
          return parseFloat(match[1]) * 100; // Convert 0.45 to 45
        }
        return 0;
      };

      const discountRateFromType = extractDiscountRate(discountType);
      const discountRate =
        parseFloat(selectedRoom.RoomPrice?.DiscountRate?.toString() || "0") ||
        discountRateFromType;

      // Use DiscountPrice if available, otherwise calculate from discount rate
      if (discountPrice > 0) {
        pricePerNight = discountPrice;
      } else if (discountRate > 0) {
        pricePerNight = originalPrice - (originalPrice * discountRate) / 100;
      } else {
        pricePerNight = originalPrice;
      }
    }

    const totalPrice = pricePerNight * totalNights * roomQty;

    return {
      perNight: pricePerNight,
      total: totalPrice,
    };
  };

  const prices = calculatePrice();
  const totalNights = totalDaysBetweenTwoDates(
    currentRoomBookingDetails?.bookingParams?.checkInDate,
    currentRoomBookingDetails?.bookingParams?.checkOutDate
  );

  let addonArray: any = [];
  let paxArray: any = [];
  let nightArray: any = [];

  currentRoomBookingDetails?.selectedRoom?.Addon?.map((item: any) =>
    addonArray.push({
      label: item.addon_desc,
      value: item.addon_code,
      price: item.addon_price,
    })
  );

  for (
    let i = 0;
    i <
    (currentRoomBookingDetails?.bookingParams?.adultPax || 0) +
      (currentRoomBookingDetails?.bookingParams?.childPax || 0);
    i++
  ) {
    paxArray.push({ value: i + 1 });
  }

  for (
    let i = 0;
    i <
    totalDaysBetweenTwoDates(
      currentRoomBookingDetails?.bookingParams?.checkInDate,
      currentRoomBookingDetails?.bookingParams?.checkOutDate
    );
    i++
  ) {
    nightArray.push({ value: i + 1 });
  }

  // const calculateLocalTax = () => {
  //   const { bookingParams } = currentRoomBookingDetails || {};

  //   if (!bookingParams) return 0;

  //   const totalNights = totalDaysBetweenTwoDates(
  //     bookingParams.checkInDate,
  //     bookingParams.checkOutDate
  //   );
  //   const roomQty = parseInt(bookingParams.roomQty) || 1;

  //   // RM 3 per room per night
  //   return 3 * totalNights * roomQty;
  // };

  // const localTax = calculateLocalTax(); // Replace the state with this calculated value

  // Update the calculateFinalPrice function to use the dynamic localTax:
  const calculateFinalPrice = () => {
    const roomPrices = calculatePrice();
    let addonsTotal = 0;
    let discountAmount = 0;

    // Calculate addons total
    checkedAddonDetails.forEach((addonDetail) => {
      const addon = addonArray.find(
        (a: any) => a.value === addonDetail.addon_code
      );
      if (addon) {
        const addonPrice = parseFloat(addon.price || "0");
        const nights = addonDetail.night || 1;
        const pax = addonDetail.pax || 1;
        addonsTotal += addonPrice * nights * pax;
      }
    });

    // Calculate discount from promo code
    if (appliedPromo && appliedPromo.eligible === "YES") {
      discountAmount = parseFloat(appliedPromo.discount_amount || "0");
    }

    // Use the dynamically calculated localTax
    // const calculatedLocalTax = calculateLocalTax();

    return {
      roomTotal: roomPrices.total,
      addonsTotal: addonsTotal,
      discountAmount: discountAmount,
      // localTax: calculatedLocalTax, // Add this to the return object
      finalTotal: Math.max(0, roomPrices.total + addonsTotal - discountAmount),
    };
  };

  const finalPrices = calculateFinalPrice();

  // Promo code functions
  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      setPromoError("Please enter a promo code");
      return;
    }

    setApplyingPromo(true);
    setPromoError("");

    try {
      const result = await checkPromotionEligibility({
        ordertype: "room",
        promotion_code: promoCode.trim(),
        amount: finalPrices.roomTotal + finalPrices.addonsTotal,
      });

      // Check if promo is eligible based on API response
      if (result.eligible === "YES") {
        setAppliedPromo(result);
        message.success(`Promo code applied! ${result.description}`);
      } else {
        setPromoError(result.description || "This promo code is not eligible");
      }
    } catch (err: any) {
      setPromoError(err.message || "Failed to apply promo code");
      message.error("Failed to apply promo code");
    } finally {
      setApplyingPromo(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoCode("");
    setPromoError("");
    message.info("Promo code removed");
  };

  // Addon Management
  const onChangeAddonInput = (addonCode: any, type: any, value: any) => {
    const newDetails: any = [...checkedAddonDetails];
    const index = newDetails.findIndex(
      (item: any) => item.addon_code === addonCode
    );

    if (index !== -1) {
      newDetails[index][type] = value;
    } else {
      newDetails.push({ addon_code: addonCode, [type]: value });
    }

    setCheckedAddonDetails(newDetails);
  };

  const onCheckSingle = (checkedValues: any, allOptionValues: any) => {
    setCheckedAddons(checkedValues);

    // Build details for backend mapping
    const details = checkedValues.map((val: string) => {
      const [addonCode, idx] = val.split("_");
      const existing = checkedAddonDetails.find((d: any) => d._uid === val);
      return existing || { _uid: val, addon_code: addonCode, night: 1, pax: 1 };
    });

    setCheckedAddonDetails(details);

    // Update "Check All" status - FIXED: Use the correct length comparison
    setCheckAllAddonsChecked(checkedValues.length === allOptionValues.length);
  };

  const onCheckAll = (checked: boolean, allAddons: any[]) => {
    if (checked) {
      const allValues = allAddons.map((addon, idx) => `${addon.value}_${idx}`);

      const details: any = allAddons.map((addon, idx) => ({
        _uid: `${addon.value}_${idx}`,
        addon_code: addon.value,
        night: 1,
        pax: 1,
      }));

      setCheckedAddons(allValues);
      setCheckedAddonDetails(details);
      setCheckAllAddonsChecked(true);
    } else {
      setCheckedAddons([]);
      setCheckedAddonDetails([]);
      setCheckAllAddonsChecked(false);
    }
  };

  const onAutofillForm = (checked: any) => {
    setFilled(checked);
    if (checked && user) {
      const userName = `${user.firstname || ""} ${user.lastname || ""}`.trim();
      const userEmail = user.email || "";
      const userPhone = user.phone || "";

      setName(userName);
      setEmail(userEmail);
      setPhone(userPhone);

      form.setFieldsValue({
        name: userName,
        email: userEmail,
        phone: userPhone,
      });
    } else {
      setName("");
      setEmail("");
      setPhone("");
      form.setFieldsValue({
        name: "",
        email: "",
        phone: "",
      });
    }
  };

  const onChangeInput = (type: any, value: any) => {
    const setters: any = {
      name: setName,
      email: setEmail,
      phone: setPhone,
      specialRequest: setSpecialRequest,
    };

    if (setters[type]) {
      setters[type](value);
    }

    // Sync with form for validation (except specialRequest)
    if (type !== "specialRequest") {
      form.setFieldsValue({
        [type]: value,
      });
    }
  };

  const findAddonIndex = (addonCode: any) => {
    return checkedAddonDetails.findIndex(
      (item: any) => item.addon_code === addonCode
    );
  };

  const getAddonValue = (addonCode: any, type: any) => {
    const index = findAddonIndex(addonCode);
    if (index !== -1) {
      return checkedAddonDetails[index][type] || 1;
    }
    return 1;
  };

  const finalCheckedAddonDetailsForBackend = (checkedValues: any) => {
    return checkedValues.map((addon: any) => {
      return {
        addon_code: addon?.addon_code.split("_")[0],
        night: addon?.night,
        pax: addon?.pax,
      };
    });
  };

  const onPayNow = async () => {
    try {
      // Validate form fields first
      await form.validateFields();

      // If validation passes, continue with existing logic
      if (!currentRoomBookingDetails?.selectedRoom) {
        // || !user?.UUID) {
        console.error("Missing required booking information");
        return;
      }

      if (!agreed) {
        message.error("Please agree to the terms and conditions");
        return;
      }

      setPayingNow(true);

      const { selectedRoom, selectedRate, bookingParams } =
        currentRoomBookingDetails;

      // Prepare rate information for backend
      const rateInfo = {
        rate_type: selectedRate?.type || "standard",
        special_rate_id:
          selectedRate?.specialRate?.id || selectedRate?.rate?.id || "",
        special_rate_title:
          selectedRate?.specialRate?.title || selectedRate?.rate?.title || "",
        rateplan_id:
          selectedRate?.type === "rateplan"
            ? selectedRate?.rateplan?.id || null
            : null,
        rateplan_amount:
          selectedRate?.type === "rateplan"
            ? selectedRate?.specialRate
              ? parseFloat(selectedRate.specialRate.amount).toFixed(2)
              : selectedRate?.rateplan
              ? parseFloat(selectedRate.rateplan.amount).toFixed(2)
              : selectedRate?.rate
              ? parseFloat(selectedRate.rate.amount).toFixed(2)
              : null
            : null,
        actual_rate_used: prices.perNight,
      };

      const params = {
        UUID: user.UUID,
        RoomId: selectedRoom.RoomId,
        Price_type: currentRoomBookingDetails?.priceType || 0,
        checkin_date: bookingParams.checkInDate,
        checkout_date: bookingParams.checkOutDate,
        adult_pax: bookingParams.adultPax,
        child_pax: bookingParams.childPax,
        room_qty: bookingParams.roomQty,
        Booking_Name: name,
        Booking_Email: email,
        Booking_Phone: phone,
        SpecialRequest: specialRequest,
        specialrate_id: rateInfo.special_rate_id,
        promotion_code: appliedPromo?.code || "", // Include promo code in booking
        referral: finalReferralCode || "", // Add referral code to booking params
        // Include rate information
        // ...rateInfo,
      };

      const addonParams = [
        ...finalCheckedAddonDetailsForBackend(checkedAddonDetails),
      ];

      console.log("check params", params);
      console.log("check addonParams", addonParams);

      try {
        const res = await createBooking(params, addonParams);
        if (res && res.payment_redirect_url) {
          setShowPaymentModal(true);
          // Give user time to read the message
          setTimeout(() => {
            window.location.replace(`${res.payment_redirect_url}`);
          }, 2500);
        }
      } catch (error) {
        console.error("Payment error:", error);
        setShowPaymentModal(false);
      } finally {
        setPayingNow(false);
      }
    } catch (error) {
      // This will catch form validation errors
      console.log("Form validation failed:", error);
      // No need to show message here - Ant Design will show the error messages automatically
    }
  };

  // Calculate actual percentage off based on price difference
  const calculatePercentageOff = (
    original: number,
    current: number
  ): number => {
    if (original <= 0 || current >= original) return 0;
    return Math.round(((original - current) / original) * 100);
  };

  // Get rate display name
  const getRateDisplayName = () => {
    const { selectedRate, selectedRoom } = currentRoomBookingDetails || {};

    if (selectedRate?.type === "special" && selectedRate.rate) {
      return selectedRate.rate.title;
    }

    if (selectedRate?.type === "rateplan" && selectedRate.rateplan) {
      return selectedRate.rateplan.title;
    }

    // Fallback to discount display for standard rates
    const originalPrice = parseFloat(
      selectedRoom?.RoomPrice?.per_room_OriginalPrice || "0"
    );
    const discountType = selectedRoom?.RoomPrice?.DiscountType || "";
    const actualPercentageOff = calculatePercentageOff(
      originalPrice,
      prices.perNight
    );

    if (actualPercentageOff > 0) {
      const discountLabel = discountType
        ? `${discountType} (${actualPercentageOff}% off)`
        : `Discounted Rate (${actualPercentageOff}% off)`;
      return discountLabel;
    }
    return "Standard Rate";
  };

  if (!currentRoomBookingDetails?.selectedRoom) {
    return (
      <div id="HomeScreen">
        <div className="container">
          <Navbar hideBanner={true} />
          <Alert
            message="No room selected. Please go back and select a room."
            type="warning"
          />
          <Button
            onClick={() => navigateWithReferral(-1)}
            style={{ marginTop: 20 }}
          >
            Back to Room Selection
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div id="HomeScreen">
      {/* @ts-ignore */}
      <Modal
        title="Processing Your Payment"
        visible={showPaymentModal}
        footer={null}
        closable={false}
        centered
        maskClosable={false}
      >
        <div style={{ textAlign: "center", padding: "20px" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔒</div>
          <Title level={4}>Secure Payment</Title>
          <Text>You're being redirected to our secure payment gateway.</Text>
          <br />
          <br />
          <Text type="secondary">
            • Please complete your payment in the new window
            <br />
            • Do not close this page until payment is complete
            <br />• You'll be redirected back here after payment
          </Text>
          <br />
          <br />
          <div style={{ marginTop: "16px" }}>
            <Spin size="large" />
          </div>
        </div>
      </Modal>
      <div className="container">
        <Navbar hideBanner={true} />
        <div>
          <Text>
            Please fill in the details below so that we can email or call you if
            we have any enquiries regarding your information or booking. Thank
            you!
          </Text>
        </div>
        <Alert
          message={
            <div>
              <div style={{ marginBottom: 8 }}>
                <Text strong>Your Selected Room:</Text>
              </div>
              <div style={{ marginBottom: 4 }}>
                <b>{currentRoomBookingDetails?.bookingParams?.roomQty}</b> x{" "}
                <b>{currentRoomBookingDetails?.selectedRoom?.RoomName}</b>
              </div>
              <div style={{ marginBottom: 4 }}>
                from{" "}
                <b>{currentRoomBookingDetails?.bookingParams?.checkInDate}</b>{" "}
                until{" "}
                <b>{currentRoomBookingDetails?.bookingParams?.checkOutDate}</b>{" "}
                ({totalNights} night{totalNights > 1 ? "s" : ""})
              </div>
              <div style={{ marginBottom: 8 }}>
                <b>
                  {currentRoomBookingDetails?.bookingParams?.adultPax} Adult
                  {currentRoomBookingDetails?.bookingParams?.adultPax > 1
                    ? "s"
                    : ""}
                </b>
                {currentRoomBookingDetails?.bookingParams?.childPax > 0 && (
                  <>
                    {" "}
                    &{" "}
                    <b>
                      {currentRoomBookingDetails?.bookingParams?.childPax} Child
                      {currentRoomBookingDetails?.bookingParams?.childPax > 1
                        ? "s"
                        : ""}
                    </b>
                  </>
                )}
              </div>
              <Divider style={{ margin: "10px 0" }} />
              <div style={{ marginBottom: 8 }}>
                <Text strong>Rate Type: {getRateDisplayName()}</Text>
              </div>
              {currentRoomBookingDetails?.selectedRate?.type === "rateplan" &&
                currentRoomBookingDetails?.selectedRate?.rateplan && (
                  <div style={{ marginBottom: 12, padding: "8px 0" }}>
                    {(() => {
                      const rp =
                        currentRoomBookingDetails.selectedRate.rateplan;
                      const badges = [];

                      if (!rp.refundable) badges.push("Non-refundable");
                      else if (rp.refundable) badges.push("Free cancellation");

                      if (rp.pay_later) badges.push("Book now, pay later");
                      else badges.push("Full payment upfront");

                      if (rp.breakfast) badges.push("Breakfast included");

                      return (
                        <div>
                          {badges.length > 0 && (
                            <div
                              style={{
                                display: "flex",
                                gap: 4,
                                flexWrap: "wrap",
                                marginBottom: 8,
                              }}
                            >
                              {badges.map((badge) => (
                                <Tag
                                  key={badge}
                                  color="default"
                                  style={{ fontSize: "11px" }}
                                >
                                  {badge}
                                </Tag>
                              ))}
                            </div>
                          )}
                          <Text
                            type="secondary"
                            style={{
                              fontSize: "12px",
                              display: "block",
                              marginBottom: 4,
                            }}
                          >
                            Rate Plan: <strong>{rp.title}</strong>
                          </Text>
                        </div>
                      );
                    })()}
                  </div>
                )}
              <div style={{ marginBottom: 8 }}>
                {(() => {
                  // Don't show discount strikethrough for rateplan selections
                  if (
                    currentRoomBookingDetails?.selectedRate?.type === "rateplan"
                  ) {
                    return (
                      <div>
                        <Text
                          strong
                          style={{ fontSize: "16px", color: "#e90056" }}
                        >
                          RM {prices.perNight.toFixed(2)} per night
                        </Text>
                        <Text
                          type="secondary"
                          style={{
                            fontSize: "12px",
                            marginTop: 4,
                            display: "block",
                          }}
                        >
                          × {totalNights} night{totalNights > 1 ? "s" : ""} ×{" "}
                          {currentRoomBookingDetails?.bookingParams?.roomQty}{" "}
                          room
                          {parseInt(
                            currentRoomBookingDetails?.bookingParams?.roomQty ||
                              "1"
                          ) > 1
                            ? "s"
                            : ""}
                        </Text>
                      </div>
                    );
                  }

                  const selectedRoom = currentRoomBookingDetails?.selectedRoom;
                  const originalPrice = parseFloat(
                    selectedRoom?.RoomPrice?.per_room_OriginalPrice || "0"
                  );
                  const discountType =
                    selectedRoom?.RoomPrice?.DiscountType || "";

                  // Calculate actual percentage off
                  const calculatePercentageOff = (
                    original: number,
                    current: number
                  ): number => {
                    if (original <= 0 || current >= original) return 0;
                    return Math.round(((original - current) / original) * 100);
                  };

                  const actualPercentageOff = calculatePercentageOff(
                    originalPrice,
                    prices.perNight
                  );
                  const hasDiscount = actualPercentageOff > 0;

                  return (
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        {hasDiscount && (
                          <Text
                            style={{
                              textDecoration: "line-through",
                              color: "#8c8c8c",
                              fontSize: "14px",
                            }}
                          >
                            RM {originalPrice.toFixed(2)}
                          </Text>
                        )}
                        <Text
                          strong
                          style={{ fontSize: "16px", color: "#e90056" }}
                        >
                          RM {prices.perNight.toFixed(2)} per night
                        </Text>
                        {hasDiscount && (
                          <Tag color="#ff4d4f" style={{ fontSize: "11px" }}>
                            {discountType ? `${discountType} - ` : ""}
                            {actualPercentageOff}% OFF
                          </Tag>
                        )}
                      </div>
                      <Text
                        type="secondary"
                        style={{
                          fontSize: "12px",
                          marginTop: 4,
                          display: "block",
                        }}
                      >
                        × {totalNights} night{totalNights > 1 ? "s" : ""} ×{" "}
                        {currentRoomBookingDetails?.bookingParams?.roomQty} room
                        {parseInt(
                          currentRoomBookingDetails?.bookingParams?.roomQty ||
                            "1"
                        ) > 1
                          ? "s"
                          : ""}
                      </Text>
                    </div>
                  );
                })()}
              </div>
              <div>
                <Text strong style={{ fontSize: "16px" }}>
                  Sub Total: RM {prices.total.toFixed(2)}
                </Text>
              </div>
            </div>
          }
          type="success"
          style={{ marginTop: "1em", marginBottom: "1em" }}
        />
        {isLoggedIn && (
          <Checkbox
            checked={filled}
            style={{ marginBottom: 20 }}
            onChange={(e) => {
              onAutofillForm(e.target.checked);
            }}
          >
            Fill with my profile's info
          </Checkbox>
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={onPayNow} // This will trigger validation before onPayNow
        >
          <Form.Item
            label="Full Name"
            name="name"
            rules={[
              { required: true, message: "Please enter your full name" },
              { min: 2, message: "Name must be at least 2 characters" },
            ]}
          >
            <Input
              value={name}
              onChange={(e) => {
                onChangeInput("name", e.target.value);
              }}
              style={{ width: "100%" }}
              placeholder="John Doe"
            />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Please enter your email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input
              value={email}
              onChange={(e) => {
                onChangeInput("email", e.target.value);
              }}
              style={{ width: "100%" }}
              placeholder="johndoe@mail.com"
            />
          </Form.Item>

          <Form.Item
            label="Phone No"
            name="phone"
            rules={[
              { required: true, message: "Please enter your phone number" },
              {
                pattern: /^[0-9]{9,11}$/,
                message: "Please enter a valid phone number (9-11 digits)",
              },
            ]}
          >
            <Input
              value={phone}
              onChange={(e) => {
                onChangeInput("phone", e.target.value);
              }}
              addonBefore={"+6"}
              placeholder="0123456789"
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            label="Special Request (optional)"
            name="specialRequest"
            tooltip={{
              title:
                "e.g, Request for extra beds / Muslim prayer mat / extra mineral water / etc...",
            }}
          >
            <TextArea
              value={specialRequest}
              onChange={(e) => onChangeInput("specialRequest", e.target.value)}
              showCount
              maxLength={150}
              style={{ width: "100%" }}
            />
          </Form.Item>

          {addonArray?.length > 0 && (
            <Form.Item label="Add-Ons (optional)">
              <div>
                <Checkbox
                  style={{ marginBottom: 10 }}
                  checked={checkAllAddonsChecked}
                  onChange={
                    (e) => onCheckAll(e.target.checked, addonArray) // Pass addonArray instead of mapped values
                  }
                >
                  Check All
                </Checkbox>
              </div>

              <Checkbox.Group
                onChange={
                  (checkedValues) => onCheckSingle(checkedValues, addonArray) // Pass addonArray instead of mapped values
                }
                value={checkedAddons}
              >
                {addonArray.map((addon: any, addonIndex: any) => (
                  <div
                    key={addon.value}
                    style={{
                      marginBottom:
                        addonIndex !== addonArray.length - 1 ? 10 : 0,
                    }}
                  >
                    <Checkbox value={`${addon.value}_${addonIndex}`}>
                      {addon.label}{" "}
                      <b>[+RM {parseFloat(addon.price || 0).toFixed(2)}]</b>
                    </Checkbox>

                    {checkedAddons.includes(`${addon.value}_${addonIndex}`) && (
                      <>
                        <div
                          style={{
                            display: "flex",
                            marginTop: 10,
                            marginLeft: 40,
                          }}
                        >
                          <div style={{ marginRight: 10, width: "100%" }}>
                            <Text style={{ color: "#000", marginBottom: 10 }}>
                              Night
                            </Text>
                            <Select
                              value={getAddonValue(addon.value, "night")}
                              onChange={(value) =>
                                onChangeAddonInput(addon.value, "night", value)
                              }
                            >
                              {nightArray.map((night: any) => (
                                <Option key={night.value} value={night.value}>
                                  {night.value}
                                </Option>
                              ))}
                            </Select>
                          </div>

                          <div style={{ width: "100%" }}>
                            <Text style={{ color: "#000", marginBottom: 10 }}>
                              Pax
                            </Text>
                            <Select
                              value={getAddonValue(addon.value, "pax")}
                              onChange={(value) =>
                                onChangeAddonInput(addon.value, "pax", value)
                              }
                            >
                              {paxArray.map((pax: any) => (
                                <Option key={pax.value} value={pax.value}>
                                  {pax.value}
                                </Option>
                              ))}
                            </Select>
                          </div>
                        </div>
                        <Divider />
                      </>
                    )}
                  </div>
                ))}
              </Checkbox.Group>
            </Form.Item>
          )}

          {/* Referral Code Section - Add this after Special Request */}
          <Form.Item label="Referral Code (optional)">
            {urlReferralCode ? (
              // If referral is bound in URL, show disabled input
              <div>
                <Input
                  value={urlReferralCode}
                  disabled
                  style={{ width: "100%" }}
                  placeholder="Referral code from URL"
                />
                <Text
                  type="secondary"
                  style={{
                    fontSize: "12px",
                    marginTop: "4px",
                    display: "block",
                  }}
                >
                  Referral code applied from your invitation link
                </Text>
              </div>
            ) : (
              // If no URL referral, allow manual input
              <Input
                value={manualReferral}
                onChange={(e) => setManualReferral(e.target.value)}
                style={{ width: "100%" }}
                placeholder="Enter referral code (optional)"
              />
            )}
          </Form.Item>

          {/* Promo Code Section */}
          <Form.Item label="Promo Code (optional)">
            <div
              style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}
            >
              <Input
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                placeholder="Enter promo code"
                disabled={applyingPromo || !!appliedPromo}
                style={{ flex: 1 }}
              />
              {appliedPromo ? (
                <Button onClick={handleRemovePromo} danger type="default">
                  Remove
                </Button>
              ) : (
                <Button
                  onClick={handleApplyPromo}
                  loading={applyingPromo}
                  type="primary"
                  disabled={!promoCode.trim()}
                >
                  Apply
                </Button>
              )}
            </div>
            {promoError && (
              <Text
                type="danger"
                style={{ fontSize: "12px", marginTop: "4px", display: "block" }}
              >
                {promoError}
              </Text>
            )}
            {appliedPromo && appliedPromo.eligible === "YES" && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 8,
                  color: "#52c41a",
                }}
              >
                <Text>Discount ({appliedPromo.code}):</Text>
                <Text>- RM {finalPrices.discountAmount.toFixed(2)}</Text>
              </div>
            )}
          </Form.Item>
        </Form>

        {/* Final Amount Summary */}
        <Card
          title="Payment Summary"
          style={{ marginBottom: 20, backgroundColor: "#f9f9f9" }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <Text>Sub Total</Text>
            <Text>RM {finalPrices.roomTotal.toFixed(2)}</Text>
          </div>

          {/* Updated Local Government Fee (LGF) display with breakdown */}
          {/* <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <div>
              <Text>Local Government Fee (LGF)</Text>
              <br />
              <Text type="secondary" style={{ fontSize: "12px" }}>
                RM 3.00 × {totalNights} nights ×{" "}
                {currentRoomBookingDetails?.bookingParams?.roomQty} rooms
              </Text>
            </div>
            <Text>RM {finalPrices.localTax.toFixed(2)}</Text>
          </div> */}

          {checkedAddons.length > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <Text>Addons Total</Text>
              <Text>RM {finalPrices.addonsTotal.toFixed(2)}</Text>
            </div>
          )}

          {appliedPromo && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
                color: "#52c41a",
              }}
            >
              <Text>Discount ({appliedPromo.code})</Text>
              <Text>- RM {finalPrices.discountAmount.toFixed(2)}</Text>
            </div>
          )}

          <Divider style={{ margin: "12px 0" }} />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text strong style={{ fontSize: "16px" }}>
              Total Amount
            </Text>
            <Text strong style={{ fontSize: "18px", color: "#e90056" }}>
              RM {finalPrices.finalTotal.toFixed(2)}
            </Text>
          </div>
        </Card>

        <Checkbox
          onChange={(e) => {
            setAgreed(e.target.checked);
          }}
          checked={agreed}
          style={{ marginRight: 10 }}
        >
          {" "}
          I agree with the <a href="#">terms and conditions</a> of this company
        </Checkbox>
        <Divider />

        <Divider />
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            style={{ marginRight: 10 }}
            onClick={() => {
              navigateWithReferral(-1);
            }}
          >
            Back
          </Button>
          <Button
            type="primary"
            onClick={() => {
              form.submit(); // This will trigger form validation and onFinish (onPayNow)
            }}
            loading={payingNow}
            disabled={!agreed}
            title={
              !agreed ? "Please agree to terms and conditions to proceed" : ""
            }
          >
            Checkout Now
          </Button>
        </div>
        <Divider />
      </div>
    </div>
  );
};

export default RoomCheckout;
